import { createI18n } from 'vue-i18n'
import serverSnapshot from './generated/en.json'

// Only English ships today. The locale-negotiation chain (User.language, the
// webview Locale postMessage, the shared dacr-locale cookie, navigator.languages)
// lands with the second locale — writing it now would be dead code, and the
// switcher would sell a language the app is 95% untranslated in.
const LOCALES = ['en'] as const
export type Locale = (typeof LOCALES)[number]
export const DEFAULT_LOCALE: Locale = 'en'

// Two dictionaries share one instance, distinguished by root namespace:
//
//   app.*   — this app's own copy, in-repo and typed.
//   others  — general/notifications/plans/billing, authored backend-side and
//             published from backend/i18n. Resolved through `tServer`, never
//             `t`, because the backend hands us values that may not be keys.
//
// The bundle is passed nested, not flattened: vue-i18n resolves a dotted key
// against nested messages natively (apps/website flattens only because it does
// its own Record lookup).
export const i18n = createI18n({
  legacy: false,
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  messages: {
    en: serverSnapshot,
  },
})

/**
 * Translates a string. Use this everywhere — not `useI18n().t` — because it
 * handles both kinds of value this app deals in:
 *
 *   t('app.billing.switchPlan')          our own copy, must exist
 *   t('plans.cards.pro.tagline')         a backend key, must exist
 *   t('Court-ready Certificate')         backend copy that isn't keyed yet
 *
 * The third case is normal, not a bug: `toI18N` in backend/payments deliberately
 * emits literal copy until a plan has been keyed, so unknown values pass through
 * unchanged rather than erroring.
 *
 * That's also why the missing-key check lives here instead of in createI18n's
 * `missing` hook — the hook can't tell those cases apart, so it would log an
 * error for every un-keyed plan and turn the Playwright suite red for something
 * that works. An `app.` key is ours, so a miss there is a real bug and is worth
 * shouting about; anything else is the backend's and is allowed to be literal.
 */
export function t(value: string): string
export function t(value: string | null | undefined): string | null
export function t(value: string | null | undefined): string | null {
  if (value == null) return null

  if (i18n.global.te(value)) return i18n.global.t(value)

  if (!import.meta.env.PROD && value.startsWith('app.')) {
    console.error(`[i18n] missing key "${value}"`)
  }

  return value
}
